import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Field, Stat, StatusBadge, Textarea } from "@/components/UI";
import { Plus, Trash, Coin, Receipt, CheckCircle, Warning, PencilSimple, Clock, UserCircle, Calendar as CalendarIcon, ChartPieSlice, Leaf, CaretRight, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CATS, PARTY_TYPES, PAYMENT_TYPES, LEAVE_REASONS, MONTHS_TR, today, fmt } from "./constants";

export function PayablesTab() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filterDir, setFilterDir] = useState("");
  const [filterType, setFilterType] = useState("");
  const [showPaid, setShowPaid] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(emptyPayable());

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/payables"), api.get("/payables/summary")]);
    setItems(r1.data);
    setSummary(r2.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter(x => {
    if (!showPaid && x.status === "paid") return false;
    if (filterDir && x.direction !== filterDir) return false;
    if (filterType && x.party_type !== filterType) return false;
    return true;
  }), [items, filterDir, filterType, showPaid]);

  const grouped = useMemo(() => {
    const g = { overdue: [], today: [], future: [], paid: [] };
    filtered.forEach(x => { g[x.status]?.push(x); });
    return g;
  }, [filtered]);

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...form, amount: Number(form.amount) };
    try {
      if (editing === "new") {
        await api.post("/payables", body);
        toast.success("Ödeme kaydı eklendi");
      } else {
        await api.patch(`/payables/${editing}`, body);
        toast.success("Güncellendi");
      }
      setEditing(null); setForm(emptyPayable()); load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const markPaid = async (p) => {
    if (!window.confirm(`${p.party_name} — ${Number(p.amount).toLocaleString("tr-TR")} ₺ ödendi olarak işaretlensin mi? Finans'a otomatik ${p.direction === "borç" ? "gider" : "gelir"} kaydı düşülecek.`)) return;
    try {
      await api.post(`/payables/${p.id}/mark-paid`);
      toast.success(p.direction === "borç" ? "Ödeme yapıldı" : "Tahsilat kaydedildi");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu ödeme kaydını silmek istediğinize emin misiniz? Bağlı finans kaydı da silinecek.")) return;
    try {
      await api.delete(`/payables/${id}`);
      toast.success("Silindi");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const edit = (p) => { setForm({ ...p, amount: p.amount }); setEditing(p.id); };

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="payables-summary">
          <Card className="!bg-[#fbeee9] border border-[#f2d4cb]">
            <div className="text-xs uppercase tracking-widest text-[#8a3a2b]">Toplam Borç</div>
            <div className="text-xl font-bold tabnum text-[#8a3a2b]">{summary.total_debt.toLocaleString("tr-TR")} ₺</div>
          </Card>
          <Card className="!bg-[#E4EEE6] border border-[#c2d6c8]">
            <div className="text-xs uppercase tracking-widest text-[#2D4A36]">Toplam Alacak</div>
            <div className="text-xl font-bold tabnum text-[#2D4A36]">{summary.total_receivable.toLocaleString("tr-TR")} ₺</div>
          </Card>
          <Card>
            <div className="text-xs uppercase tracking-widest text-[#6b746c]">Net Pozisyon</div>
            <div className={`text-xl font-bold tabnum ${summary.net_position >= 0 ? "text-[#2D4A36]" : "text-[#C45B45]"}`}>{summary.net_position >= 0 ? "+" : ""}{summary.net_position.toLocaleString("tr-TR")} ₺</div>
          </Card>
          <Card>
            <div className="text-xs uppercase tracking-widest text-[#6b746c]">Vadesi Geçmiş Borç</div>
            <div className="text-xl font-bold tabnum text-[#C45B45]">{summary.overdue_debt.toLocaleString("tr-TR")} ₺</div>
          </Card>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="payable-new-btn" onClick={() => { setForm(emptyPayable()); setEditing("new"); }}>
          <Plus size={14} weight="bold" className="inline mr-1" /> Yeni Ödeme/Tahsilat
        </Button>
        <div className="flex-1" />
        <Select value={filterDir} onChange={e => setFilterDir(e.target.value)} data-testid="payable-filter-dir" className="!w-auto">
          <option value="">Tüm yönler</option>
          <option value="borç">Sadece Borçlar</option>
          <option value="alacak">Sadece Alacaklar</option>
        </Select>
        <Select value={filterType} onChange={e => setFilterType(e.target.value)} data-testid="payable-filter-type" className="!w-auto">
          <option value="">Tüm taraflar</option>
          {PARTY_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
        </Select>
        <label className="text-sm flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showPaid} onChange={e => setShowPaid(e.target.checked)} data-testid="payable-show-paid" />
          Ödenmişleri göster
        </label>
      </div>

      {/* Form (inline) */}
      {editing && (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <h3 className="heading text-lg">{editing === "new" ? "Yeni Ödeme/Tahsilat Kaydı" : "Kayıt Düzenle"}</h3>
            <Button type="button" onClick={() => setEditing(null)} className="!bg-transparent !text-[#6b746c] border border-[#E5E2DC]">İptal</Button>
          </div>
          <form onSubmit={submit} className="grid md:grid-cols-3 gap-3">
            <Field label="Yön *">
              <Select data-testid="payable-direction" value={form.direction} onChange={e => setForm({ ...form, direction: e.target.value })}>
                <option value="borç">Borç (biz ödeyeceğiz)</option>
                <option value="alacak">Alacak (bize ödenecek)</option>
              </Select>
            </Field>
            <Field label="Taraf Tipi *">
              <Select data-testid="payable-party-type" value={form.party_type} onChange={e => setForm({ ...form, party_type: e.target.value })}>
                {PARTY_TYPES.map(p => <option key={p} value={p}>{p}</option>)}
              </Select>
            </Field>
            <Field label="Taraf Adı *"><Input data-testid="payable-party-name" required value={form.party_name} onChange={e => setForm({ ...form, party_name: e.target.value })} placeholder="Örn: Yem Ltd, Ali Usta" /></Field>
            <Field label="Tutar (₺) *"><Input data-testid="payable-amount" type="number" step="0.01" required value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label="Vade Tarihi *"><Input data-testid="payable-due-date" type="date" required value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></Field>
            <Field label="Fatura No"><Input data-testid="payable-invoice" value={form.invoice_no || ""} onChange={e => setForm({ ...form, invoice_no: e.target.value })} placeholder="F-2026-001" /></Field>
            <div className="md:col-span-3">
              <Field label="Açıklama"><Textarea rows={2} value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} /></Field>
            </div>
            <div className="md:col-span-3">
              <Button type="submit" data-testid="payable-save"><Plus size={14} weight="bold" className="inline mr-1" /> Kaydet</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Grouped table */}
      {["overdue", "today", "future", ...(showPaid ? ["paid"] : [])].map(group => (
        <PayableGroup key={group} group={group} items={grouped[group] || []} onPaid={markPaid} onEdit={edit} onDelete={remove} />
      ))}

      {filtered.length === 0 && !editing && (
        <Card className="text-center py-12">
          <Receipt size={40} weight="duotone" className="mx-auto text-[#8a938c] mb-3" />
          <div className="text-[#6b746c] mb-1">Henüz ödeme/tahsilat kaydı yok</div>
          <div className="text-xs text-[#8a938c]">"Yeni Ödeme/Tahsilat" ile başlayın.</div>
        </Card>
      )}
    </div>
  );
}

function emptyPayable() {
  return { party_type: "tedarikçi", party_name: "", direction: "borç", amount: "", due_date: today(), invoice_no: "", description: "" };
}

const GROUP_META = {
  overdue: { label: "Geciken", icon: Warning, tone: "bg-[#fbeee9] border-[#f2d4cb]", textTone: "text-[#8a3a2b]" },
  today: { label: "Bugün", icon: Clock, tone: "bg-[#F5E9D3] border-[#e0c890]", textTone: "text-[#8B6914]" },
  future: { label: "Gelecek", icon: Coin, tone: "bg-[#E4EEE6] border-[#c2d6c8]", textTone: "text-[#2D4A36]" },
  paid: { label: "Ödenmiş", icon: CheckCircle, tone: "bg-[#F5F2EC] border-[#E5E2DC]", textTone: "text-[#6b746c]" },
};

function PayableGroup({ group, items, onPaid, onEdit, onDelete }) {
  if (!items.length) return null;
  const meta = GROUP_META[group];
  const Icon = meta.icon;
  const total = items.reduce((s, x) => s + Number(x.amount || 0), 0);

  return (
    <Card className="!p-0 overflow-hidden" data-testid={`payable-group-${group}`}>
      <div className={`px-4 py-3 border-b border-[#E5E2DC] flex items-center gap-2 ${meta.tone}`}>
        <Icon size={18} weight="duotone" className={meta.textTone} />
        <h3 className={`heading text-lg ${meta.textTone}`}>{meta.label}</h3>
        <span className="text-xs text-[#6b746c] ml-2">({items.length} kayıt · toplam {total.toLocaleString("tr-TR")} ₺)</span>
      </div>
      <table className="data-table">
        <thead><tr>
          <th>Vade</th><th>Taraf</th><th>Tip</th><th>Yön</th><th>Fatura</th>
          <th className="text-right">Tutar</th><th className="text-right">Kalan Gün</th><th></th>
        </tr></thead>
        <tbody>
          {items.map(x => (
            <tr key={x.id} data-testid={`payable-row-${x.id}`}>
              <td className="tabnum">{x.due_date}</td>
              <td className="font-semibold">{x.party_name}</td>
              <td className="capitalize text-[#6b746c]">{x.party_type}</td>
              <td>
                <span className={`text-xs px-2 py-0.5 rounded ${x.direction === "borç" ? "bg-[#fbeee9] text-[#8a3a2b]" : "bg-[#E4EEE6] text-[#2D4A36]"}`}>
                  {x.direction === "borç" ? "↓ borç" : "↑ alacak"}
                </span>
              </td>
              <td className="text-xs text-[#6b746c]">{x.invoice_no || "—"}</td>
              <td className={`text-right tabnum font-semibold ${x.direction === "borç" ? "text-[#8a3a2b]" : "text-[#2D4A36]"}`}>
                {x.direction === "borç" ? "−" : "+"} {Number(x.amount).toLocaleString("tr-TR")} ₺
              </td>
              <td className={`text-right tabnum text-xs ${x.days_left < 0 ? "text-[#C45B45] font-semibold" : x.days_left === 0 ? "text-[#8B6914] font-semibold" : "text-[#6b746c]"}`}>
                {x.status === "paid" ? `Ödendi: ${x.paid_at}` : x.days_left < 0 ? `${Math.abs(x.days_left)} gün gecikti` : x.days_left === 0 ? "bugün" : `${x.days_left} gün sonra`}
              </td>
              <td>
                <div className="flex gap-1 justify-end">
                  {x.status !== "paid" && (
                    <button onClick={() => onPaid(x)} data-testid={`payable-paid-${x.id}`}
                      className="p-1.5 rounded hover:bg-[#E4EEE6] text-[#2D4A36]" title={x.direction === "borç" ? "Ödendi olarak işaretle" : "Tahsil edildi olarak işaretle"}>
                      <CheckCircle size={16} weight="duotone" />
                    </button>
                  )}
                  <button onClick={() => onEdit(x)} data-testid={`payable-edit-${x.id}`}
                    className="p-1.5 rounded hover:bg-[#F5F2EC]" title="Düzenle">
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => onDelete(x.id)} data-testid={`payable-del-${x.id}`}
                    className="p-1.5 rounded hover:bg-[#fbeee9] text-[#C45B45]" title="Sil">
                    <Trash size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// -------- PERSONNEL TAB --------
